import { useTranslation } from 'react-i18next'

import { Calendar } from 'primereact/calendar'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'

import { DateTemplate } from '../../../../../../components/DateTemplate'
import { Invoice, InvoiceItem } from '../../../../../../data/models'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'

type Props = {
  currency: Invoice['OrderCurrency']
  invoiceItem?: InvoiceItem
  isLoading: boolean
}

export const InfoForm: React.FC<Props> = ({
  currency,
  invoiceItem,
  isLoading,
}) => {
  const { i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  // itemId
  const itemIdLabel = <label htmlFor="itemId" className="text-sm"></label>

  const itemIdComponent = (
    <InputText
      id="itemId"
      className="w-full"
      value={invoiceItem?.ItemId}
      disabled
    />
  )

  const itemIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={itemIdComponent}
      label={itemIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label htmlFor="confirmationItemId" className="text-sm"></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      value={invoiceItem?.ConfirmationItemId}
      className="w-full"
      disabled
    />
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      isLoading={isLoading}
    />
  )

  // countryOfOrigin
  const countryOfOriginLabel = (
    <label htmlFor="countryOfOrigin" className="text-sm"></label>
  )

  const countryOfOriginComponent = (
    <InputText
      id="countryOfOrigin"
      className="w-full"
      value={invoiceItem?.CountryOfOrigin}
      disabled
    />
  )

  const countryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={countryOfOriginComponent}
      label={countryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label htmlFor="confirmationCountryOfOrigin" className="text-sm"></label>
  )

  const confirmationCountryOfOriginComponent = (
    <InputText
      id="confirmationCountryOfOrigin"
      value={invoiceItem?.ConfirmationCountryOfOrigin}
      className="w-full"
      disabled
    />
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // gtin
  const gtinLabel = <label htmlFor="gtin" className="text-sm"></label>

  const gtinComponent = (
    <InputText
      id="gtin"
      className="w-full"
      value={invoiceItem?.GTIN}
      disabled
    />
  )

  const gtinWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={gtinComponent}
      label={gtinLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={invoiceItem?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductId
  const confirmationSellerProductIdLabel = (
    <label htmlFor="confirmationSellerProductId" className="text-sm"></label>
  )

  const confirmationSellerProductIdComponent = (
    <InputText
      id="confirmationSellerProductId"
      value={invoiceItem?.ConfirmationSellerProductId}
      className="w-full"
      disabled
    />
  )

  const confirmationSellerProductIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationSellerProductIdComponent}
      label={confirmationSellerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={invoiceItem?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductDescription
  const confirmationSellerProductDescriptionLabel = (
    <label
      htmlFor="confirmationSellerProductDescription"
      className="text-sm"
    ></label>
  )

  const confirmationSellerProductDescriptionComponent = (
    <InputText
      id="confirmationSellerProductDescription"
      value={invoiceItem?.ConfirmationSellerProductDescription}
      className="w-full"
      disabled
    />
  )

  const confirmationSellerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationSellerProductDescriptionComponent}
      label={confirmationSellerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  //  quantity
  const quantityLabel = <label htmlFor="quantity" className="text-sm"></label>

  const quantityComponent = (
    <InputNumber
      id="quantity"
      className="w-full"
      value={invoiceItem?.Quantity}
      locale={language}
      disabled
    />
  )

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={quantityComponent}
      label={quantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label htmlFor="confirmationQuantity" className="text-sm"></label>
  )

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={invoiceItem?.ConfirmationQuantity}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      isLoading={isLoading}
    />
  )

  // unit
  const unitLabel = <label htmlFor="unit" className="text-sm"></label>

  const unitComponent = (
    <InputText
      id="unit"
      className="w-full"
      value={invoiceItem?.Unit}
      disabled
    />
  )

  const unitWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={unitComponent}
      label={unitLabel}
      isLoading={isLoading}
    />
  )

  // confirmationUnit
  const confirmationUnitLabel = (
    <label htmlFor="confirmationUnit" className="text-sm"></label>
  )

  const confirmationUnitComponent = (
    <InputText
      id="confirmationUnit"
      value={invoiceItem?.ConfirmationUnit}
      className="w-full"
      disabled
    />
  )

  const confirmationUnitWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationUnitComponent}
      label={confirmationUnitLabel}
      isLoading={isLoading}
    />
  )

  // priceBaseQuantity
  const priceBaseQuantityLabel = (
    <label htmlFor="priceBaseQuantity" className="text-sm"></label>
  )

  const priceBaseQuantityComponent = (
    <InputNumber
      id="priceBaseQuantity"
      className="w-full"
      value={invoiceItem?.PriceBaseQuantity}
      locale={language}
      disabled
    />
  )

  const priceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={priceBaseQuantityComponent}
      label={priceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceBaseQuantity
  const confirmationPriceBaseQuantityLabel = (
    <label htmlFor="confirmationPriceBaseQuantity" className="text-sm"></label>
  )

  const confirmationPriceBaseQuantityComponent = (
    <InputNumber
      id="confirmationPriceBaseQuantity"
      value={invoiceItem?.ConfirmationPriceBaseQuantity}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationPriceBaseQuantityComponent}
      label={confirmationPriceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // priceUnitOfMeasure
  const priceUnitOfMeasureLabel = (
    <label htmlFor="priceUnitOfMeasure" className="text-sm"></label>
  )

  const priceUnitOfMeasureComponent = (
    <InputText
      id="priceUnitOfMeasure"
      className="w-full"
      value={invoiceItem?.PriceUnitOfMeasure}
      disabled
    />
  )

  const priceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={priceUnitOfMeasureComponent}
      label={priceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceUnitOfMeasure
  const confirmationPriceUnitOfMeasureLabel = (
    <label htmlFor="confirmationPriceUnitOfMeasure" className="text-sm"></label>
  )

  const confirmationPriceUnitOfMeasureComponent = (
    <InputText
      id="confirmationPriceUnitOfMeasure"
      value={invoiceItem?.ConfirmationPriceUnitOfMeasure?.toUpperCase()}
      className="w-full"
      disabled
    />
  )

  const confirmationPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationPriceUnitOfMeasureComponent}
      label={confirmationPriceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // price
  const priceLabel = <label htmlFor="price" className="text-sm"></label>
  const priceComponent = (
    <InputNumber
      id="price"
      value={invoiceItem?.Price}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={priceComponent}
      label={priceLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPrice
  const confirmationPriceLabel = (
    <label htmlFor="confirmationPrice" className="text-sm"></label>
  )

  const confirmationPriceComponent = (
    <InputNumber
      id="confirmationPrice"
      value={invoiceItem?.ConfirmationPrice}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationPriceComponent}
      label={confirmationPriceLabel}
      isLoading={isLoading}
    />
  )

  // priceSum
  const priceSumLabel = <label htmlFor="priceSum" className="text-sm"></label>

  const priceSumComponent = (
    <InputNumber
      id="priceSum"
      value={invoiceItem?.PriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={priceSumComponent}
      label={priceSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceSum
  const confirmationPriceSumLabel = (
    <label htmlFor="confirmationPriceSum" className="text-sm"></label>
  )

  const confirmationPriceSumComponent = (
    <InputNumber
      id="confirmationPriceSum"
      mode="currency"
      currency={currency}
      value={invoiceItem?.ConfirmationPriceSum}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationPriceSumComponent}
      label={confirmationPriceSumLabel}
      isLoading={isLoading}
    />
  )

  // allowanceChargeSum
  const allowanceChargeSumLabel = (
    <label htmlFor="allowanceChargeSum" className="text-sm"></label>
  )

  const allowanceChargeSumComponent = (
    <InputNumber
      id="allowanceChargeSum"
      value={invoiceItem?.AllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const allowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={allowanceChargeSumComponent}
      label={allowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAllowanceChargeSum
  const confirmationAllowanceChargeSumLabel = (
    <label htmlFor="confirmationAllowanceChargeSum" className="text-sm"></label>
  )

  const confirmationAllowanceChargeSumComponent = (
    <InputNumber
      id="confirmationAllowanceChargeSum"
      mode="currency"
      currency={currency}
      value={invoiceItem?.ConfirmationAllowanceChargeSum}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationAllowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationAllowanceChargeSumComponent}
      label={confirmationAllowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // lineItemAmount
  const lineItemAmountLabel = (
    <label htmlFor="lineItemAmount" className="text-sm"></label>
  )

  const lineItemAmountComponent = (
    <InputNumber
      id="lineItemAmount"
      value={invoiceItem?.LineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={lineItemAmountComponent}
      label={lineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationLineItemAmount
  const confirmationLineItemAmountLabel = (
    <label htmlFor="confirmationLineItemAmount" className="text-sm"></label>
  )

  const confirmationLineItemAmountComponent = (
    <InputNumber
      id="confirmationLineItemAmount"
      mode="currency"
      currency={currency}
      value={invoiceItem?.ConfirmationLineItemAmount}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationLineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationLineItemAmountComponent}
      label={confirmationLineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // deliveryDate
  const deliveryDateLabel = (
    <label htmlFor="deliveryDate" className="text-sm"></label>
  )
  const deliveryDate = invoiceItem?.DeliveryDate
    ? new Date(invoiceItem?.DeliveryDate)
    : null

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={deliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label htmlFor="confirmationDeliveryDate" className="text-sm"></label>
  )

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={new Date(invoiceItem?.ConfirmationDeliveryDate!)}
      className="w-full"
      showIcon
      minDate={new Date()}
      dateTemplate={(date: any) => (
        <DateTemplate
          date={date}
          desiredDeliveryDate={invoiceItem?.DeliveryDate}
        />
      )}
      locale={language}
      disabled
    />
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
    />
  )

  // confirmationReasonCode
  const confirmationReasonCodeLabel = (
    <label htmlFor="confirmationReasonCode" className="text-sm"></label>
  )

  const confirmationReasonCodeComponent = (
    <Dropdown
      id="confirmationReasonCode"
      className="w-full"
      value={invoiceItem?.ConfirmationReason?.Code}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const confirmationReasonCodeWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationReasonCodeComponent}
      label={confirmationReasonCodeLabel}
    />
  )

  // confirmationReasonDescription
  const confirmationReasonDescriptionLabel = (
    <label htmlFor="confirmationReasonDescription" className="text-sm"></label>
  )

  const confirmationReasonDescriptionComponent = (
    <InputText
      id="confirmationReasonDescription"
      value={invoiceItem?.ConfirmationReason?.Description}
      className="w-full"
      disabled
    />
  )

  const confirmationReasonDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={confirmationReasonDescriptionComponent}
      label={confirmationReasonDescriptionLabel}
    />
  )

  // notes
  const notesLabel = <label htmlFor="notes" className="text-sm"></label>

  const notesComponent = (
    <InputTextarea
      id="notes"
      rows={5}
      value={invoiceItem?.Notes}
      className="w-full"
      disabled
    />
  )

  const notesWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItem}
      component={notesComponent}
      label={notesLabel}
    />
  )

  return (
    <>
      {/* itemId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{itemIdWrapper}</div>
        <div className="col-12 md:col-6">{confirmationItemIdWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* countryOfOrigin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{countryOfOriginWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationCountryOfOriginWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* gtin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{gtinWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductIdWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductIdWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductDescription */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductDescriptionWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* quantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{quantityWrapper}</div>

        <div className="col-12 md:col-6">{confirmationQuantityWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* unit */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{unitWrapper}</div>

        <div className="col-12 md:col-6">{confirmationUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceBaseQuantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceBaseQuantityWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceBaseQuantityWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* priceUnitOfMeasure */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceUnitOfMeasureWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceUnitOfMeasureWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* price */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* confirmationPriceSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{priceSumWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceSumWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* allowanceChargeSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{allowanceChargeSumWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationAllowanceChargeSumWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* ItemAmount */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{lineItemAmountWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationLineItemAmountWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* deliveryDate */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{deliveryDateWrapper}</div>

        <div className="col-12 md:col-6">{confirmationDeliveryDateWrapper}</div>
      </div>

      {/* code */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{confirmationReasonCodeWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationReasonDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* notes */}
      <div className="formgrid grid">
        <div className="col-12">{notesWrapper}</div>
      </div>
    </>
  )
}
